import { useCallback, useEffect, useState } from "react";
import http from "../../../utils/http";
import { errorToastMessage } from "../../../utils/toast";
import { noValueTypes } from "../questionTypes";
import { Question } from "../../../types/StudyFormById.types";

export type DependencyFormListItem = {
  phaseId: string;
  formId: string;
  label: string;
  position: number;
  status?: string;
};

type UseDependencyParentPickerParams = {
  parentFormId?: string;
  parentQuestionId?: string;
  excludeQuestionId?: string;
  studyId?: string;
  formCategory?: string;
  formLevel?: string;
  currentFormId?: string;
  currentFormLabel?: string;
};

function buildFormListFromPhases(
  phases: any[],
  options?: { excludeStatuses?: string[] }
): DependencyFormListItem[] {
  const excludeStatuses = new Set(options?.excludeStatuses || []);
  let newObjectsArray: DependencyFormListItem[] = [];
  const sortedPhases = [...phases].sort(
    (a: { position: number }, b: { position: number }) =>
      a.position - b.position
  );
  sortedPhases.forEach((phase: any) => {
    const parentId = phase.id;
    const forms: DependencyFormListItem[] = [];
    phase.phaseForms.forEach((phaseForm: any) => {
      const status = phaseForm.form?.status;
      if (status && excludeStatuses.has(status)) {
        return;
      }
      forms.push({
        phaseId: parentId,
        formId: phaseForm.form.id,
        label: `${phase.name} - ${phaseForm.form.name}`,
        position: phaseForm.position,
        status: status || undefined,
      });
    });
    forms.sort(
      (a: { position: number }, b: { position: number }) =>
        a.position - b.position
    );
    newObjectsArray = newObjectsArray.concat(forms);
  });
  return newObjectsArray;
}

export function useDependencyParentPicker({
  parentFormId,
  parentQuestionId,
  excludeQuestionId,
  studyId,
  formCategory,
  formLevel,
  currentFormId,
  currentFormLabel = "Current form",
}: UseDependencyParentPickerParams) {
  const [isLoading, setIsLoading] = useState(false);
  const [formList, setFormList] = useState<DependencyFormListItem[]>([]);
  const [questionList, setQuestionList] = useState<Question[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<Question>();

  const fetchFormList = useCallback(async () => {
    if (!studyId || formLevel !== "study") {
      return;
    }

    if (formCategory === "visit") {
      setIsLoading(true);
      http
        .get(`/study/${studyId}/study-phase`, {
          params: { category: formCategory },
        })
        .then(({ data }) => {
          setFormList(buildFormListFromPhases(data.data));
          setIsLoading(false);
        })
        .catch((err) => {
          errorToastMessage(err as Error);
          setIsLoading(false);
        });
      return;
    }

    if (formCategory === "survey") {
      setIsLoading(true);
      http
        .get(`/study/${studyId}/study-phase`, {
          params: { category: "screening" },
        })
        .then(({ data }) => {
          const screeningForms = buildFormListFromPhases(data.data || [], {
            excludeStatuses: ["draft"],
          });
          const currentFormEntry: DependencyFormListItem | null =
            currentFormId
              ? {
                  phaseId: "",
                  formId: currentFormId,
                  label: currentFormLabel,
                  position: -1,
                }
              : null;

          const withoutDuplicateCurrent = currentFormId
            ? screeningForms.filter((f) => f.formId !== currentFormId)
            : screeningForms;

          setFormList(
            currentFormEntry
              ? [currentFormEntry, ...withoutDuplicateCurrent]
              : withoutDuplicateCurrent
          );
          setIsLoading(false);
        })
        .catch((err) => {
          errorToastMessage(err as Error);
          setIsLoading(false);
        });
    }
  }, [
    studyId,
    formCategory,
    formLevel,
    currentFormId,
    currentFormLabel,
  ]);

  const fetchQuestionList = useCallback(async () => {
    if (!parentFormId) {
      setQuestionList([]);
      return;
    }

    const prefix =
      formLevel === "platform" ? "/admin/registry/" : `/study/${studyId}/`;

    await http
      .get(`${prefix}forms/${parentFormId}`)
      .then(({ data }) => {
        const filteredQuestions = data.data?.questions
          ?.filter(
            (q: Question) =>
              q.id !== excludeQuestionId && !noValueTypes.includes(q.type)
          )
          .sort(
            (a: { position: number }, b: { position: number }) =>
              a.position - b.position
          );
        setQuestionList(filteredQuestions || []);
      })
      .catch((err) => {
        errorToastMessage(err as Error);
      });
  }, [parentFormId, excludeQuestionId, studyId, formLevel]);

  useEffect(() => {
    fetchFormList();
  }, [fetchFormList]);

  useEffect(() => {
    fetchQuestionList();
  }, [fetchQuestionList]);

  useEffect(() => {
    if (questionList?.length && parentQuestionId) {
      setSelectedQuestion(
        questionList.find((q) => q.id === parentQuestionId)
      );
    } else if (!parentQuestionId) {
      setSelectedQuestion(undefined);
    }
  }, [questionList, parentQuestionId]);

  return {
    isLoading,
    formList,
    questionList,
    selectedQuestion,
    setSelectedQuestion,
  };
}

export function getDependencyParentValueType(question?: Question): string {
  let qType = question?.type || "";

  if (
    qType === "calculated_field" &&
    question?.properties?.responseType === "string"
  ) {
    qType = "calculated_field_text";
  }

  return qType;
}
